import { useQuery } from "@tanstack/react-query";

import { fetchErrorReport, fetchEnhancingListings } from "api/error-report-api";

import { toCurrency } from "shared/helpers";

export interface IErrorReportData {
  /* eslint-disable @typescript-eslint/naming-convention */
  brand?: string;
  color?: string | null;
  date_created: string;
  error?: string;
  name?: string;
  product_id?: string | number;
  quantity: number;
  source: string;
  status: "PENDING" | "ERROR" | "IGNORED";
  total_wholesale_cost?: number;
  wholesale_cost?: number | string;
  /* eslint-enable @typescript-eslint/naming-convention */
}

export const useErrorReport = ({ retailerId }: { retailerId: string }) => {
  const { data: errorData, ...errorRest } = useQuery<IErrorReportData[] | null>(
    {
      queryKey: ["error-report", retailerId],
      queryFn: () => fetchErrorReport({ retailerId }),
      enabled: Boolean(retailerId)
    }
  );
  const { data: enhancedData, ...enhancedRest } = useQuery<IErrorReportData[] | null>(
    {
      queryKey: ["enhancing-listings", retailerId],
      queryFn: () => fetchEnhancingListings({ retailerId }),
      enabled: Boolean(retailerId)
    }
  );

  if (!errorData || !enhancedData) {
    return {
      data: { error: [], pending: [] },
      isLoading: true
    };
  }

  const error: IErrorReportData[] = [];
  const pending: IErrorReportData[] = [];
  const ERROR_CODE_REGEX = /\(\d+\)\s*/g;

  for (const row of errorData) {
    const total = toCurrency(row.total_wholesale_cost);
    const value = {
      ...row,
      originalError: row.error,
      error: typeof row.error === "string"
          ? row.error.replace(ERROR_CODE_REGEX, "").replace(/\s+/g, " ").trim()
          : "",
      total,
      date: row.date_created,
      status: row.status
    };
    error.push(value);
  }

  for (const row of enhancedData) {
    const total = toCurrency(row.total_wholesale_cost);
    const value = {
      ...row,
      total,
      date: row.date_created,
      status: row.status,
    };
    pending.push(value);
  }

  return {
    data: { error, pending },
    ...errorRest,
    ...enhancedRest,
    isLoading: errorRest.isLoading || enhancedRest.isLoading,
    refetch: async () => { await errorRest.refetch() ; await enhancedRest.refetch(); },
  };
};
